import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { LayoutDashboard, RefreshCw, Loader2, ArrowRight, TrendingUp, Instagram, Facebook } from "lucide-react";
import { toast } from "sonner";
import { useCompany } from "@/context/CompanyContext";

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("es-ES", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
}

export default function CrossEmpresaDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { setActiveId } = useCompany();

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/social/dashboard/cross-empresa");
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading) return <div className="p-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-brand-emerald" /></div>;
  if (!data) return null;

  const totals = (data.rows || []).reduce((a, r) => ({
    posts_total: a.posts_total + (r.posts_total || 0),
    posts_published: a.posts_published + (r.posts_published || 0),
    posts_failed: a.posts_failed + (r.posts_failed || 0),
    real_connections: a.real_connections + (r.real_connections || 0),
    contacts: a.contacts + (r.contacts || 0),
  }), { posts_total: 0, posts_published: 0, posts_failed: 0, real_connections: 0, contacts: 0 });

  return (
    <div className="space-y-8 animate-fade-in-up" data-testid="cross-empresa-dashboard">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="divider-gold mb-3" />
          <h1 className="font-serif-display text-4xl text-brand-ink font-semibold flex items-center gap-3">
            <span className="inline-flex h-10 w-10 rounded-lg items-center justify-center bg-brand-emerald text-white">
              <LayoutDashboard className="h-5 w-5" strokeWidth={1.8} />
            </span>
            Dashboard Cross-Empresa
          </h1>
          <p className="text-sm text-stone-500 mt-2">
            Operación de todas tus empresas desde una sola vista. Solo datos reales.
          </p>
        </div>
        <Button onClick={load} className="bg-brand-emerald text-white hover:bg-brand-emerald-dark" data-testid="btn-refresh-cross">
          <RefreshCw className="h-4 w-4 mr-2" /> Actualizar
        </Button>
      </div>

      {/* Totales */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          ["Empresas", data.companies_count],
          ["Posts totales", totals.posts_total],
          ["Publicados", totals.posts_published],
          ["Conexiones reales", totals.real_connections],
          ["Contactos", totals.contacts],
        ].map(([label, value]) => (
          <div key={label} className="card-elevated p-4">
            <div className="text-xs uppercase tracking-widest text-stone-500 font-semibold">{label}</div>
            <div className="font-serif-display text-3xl font-semibold text-brand-ink mt-1">{value}</div>
          </div>
        ))}
      </div>

      {/* Tabla */}
      <div className="card-elevated overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Empresa</TableHead>
              <TableHead className="text-right">Posts</TableHead>
              <TableHead className="text-right">Publicados</TableHead>
              <TableHead className="text-right">Programados</TableHead>
              <TableHead className="text-right">Fallidos</TableHead>
              <TableHead className="text-right">Cuentas reales</TableHead>
              <TableHead className="text-right">Contactos</TableHead>
              <TableHead>Última publicación</TableHead>
              <TableHead className="w-16"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(data.rows || []).map((r) => (
              <TableRow key={r.company_id} data-testid={`row-${r.company_id}`}>
                <TableCell className="font-medium text-brand-ink">{r.company_name}</TableCell>
                <TableCell className="text-right font-mono">{r.posts_total}</TableCell>
                <TableCell className="text-right font-mono text-brand-emerald-dark">{r.posts_published}</TableCell>
                <TableCell className="text-right font-mono">{r.posts_scheduled}</TableCell>
                <TableCell className="text-right font-mono text-destructive">{r.posts_failed}</TableCell>
                <TableCell className="text-right">
                  {r.real_connections > 0 ? (
                    <Badge className="bg-brand-emerald-soft text-brand-emerald-dark border-brand-emerald/30">{r.real_connections}</Badge>
                  ) : <span className="text-stone-400 text-xs">0</span>}
                </TableCell>
                <TableCell className="text-right font-mono">{r.contacts}</TableCell>
                <TableCell className="text-xs text-stone-500">
                  {r.last_published_at ? (
                    <div>
                      <div>{fmt(r.last_published_at)}</div>
                      <div className="text-[10px] uppercase tracking-widest text-stone-400">{r.last_published_platform}</div>
                    </div>
                  ) : <span className="text-stone-400">—</span>}
                </TableCell>
                <TableCell>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => { setActiveId(r.company_id); toast.success(`Activaste ${r.company_name}`); }}
                    data-testid={`activate-${r.company_id}`}
                    title="Activar esta empresa"
                  >
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
